<template>
  <div>
    <SidebarLayout
      :breadcrumb="breadcrumb"
      :rootPath="rootPath"
      :pageTitle="pageTitle"
      class="absolute-rate-page"
    >
      <template #page-description>
        <p>
          Aqui você pode atualizar valores e descontos base, e visualizá-los no
          <router-link
            class="text-link"
            :to="`/hoteis/calendario-tarifas-allotment/${hotelId}`"
            >Calendário de tarifas e allotment</router-link
          >. Os valores base influenciam em todas as flutuações.
        </p>
      </template>

      <template #sidebar>
        <!-- NOME DO HOTEL -->
        <InfoPanel height="103" class="info-panel">
          <template #content>
            <Heading class="info-panel--title heading--size-2">
              {{ hotel.nome }}
            </Heading>
          </template>
        </InfoPanel>
        <SidebarBlock class="page-actions">
          <!-- MENU DOS HOTÉIS -->
          <PmwHotelNav
            :links="menuLinks"
            :hide="['diferenciais', 'banners']"
            active="tarifabase"
            class="menu-nav"
          />

          <Heading>Ações da página</Heading>

          <div v-if="selectedTab === 0 || selectedTab === 1">
            <Button
              :disabled="disableApplyToCalendar"
              tooltipText="Necessário selecionar datas e quartos para continuar."
              @click="confirmRatesUpdate()"
            >
              Aplicar ao calendário
            </Button>

            <Button
              :disabled="disablePreviewButton"
              layout="outline"
              tooltipText="Necessário selecionar datas e quartos para visualizar."
              @click="showPreviewModal = true"
            >
              Pré-visualizar
            </Button>
          </div>

          <!-- BLACKOUT EM MASSA  -->
          <div v-else>
            <Button
              :disabled="disableApplyBlackout"
              tooltipText="Selecione ao menos uma data e uma tarifa para aplicar o blackout."
              @click="showBlackoutModal = true"
            >
              Aplicar Blackout
            </Button>
            <Button layout="outline">
              Voltar
            </Button>
          </div>
        </SidebarBlock>
      </template>

      <template #content>
        <Tabs @changeTab="changeTab">
          <Tab title="Atualização de valores" active>
            <RatesUpdate v-model="rates" :rooms="rooms" />
          </Tab>

          <Tab title="Disponibilidade e restrições">
            <RatesRestrictionsClosures
              :rooms="rooms"
              :conflictRestrictions="conflictRestrictions"
              @changeAllotments="restrictions.allotment = $event"
              @changeRestrictions="restrictions.restricoes = $event"
            />

            <AlertModal
              ref="restricionsAlert"
              v-model="showRestricionsAlert"
              icon="alert"
              title="Ops!"
              confirmButtonText="Voltar e conferir"
              :cancelButtonText="null"
              :buttonClose="true"
            >
              <template #text>
                Alguma de suas restrições entrou em conflito com outra. <br />
                Por favor, confira as linhas em vermelho e tente novamente.
              </template>
            </AlertModal>
          </Tab>

          <Tab title="Blackout em Massa">
            <MassBlackout
              :saveSucces="blackoutSuccess"
              @datesSelection="selectedBlackoutDates = $event"
              @ratesSelection="selectedBlackoutRates = $event"
            />
          </Tab>
        </Tabs>

        <!-- MODAL DE CONFIRMAÇÃO -->
        <ConfirmationModal
          v-model="showConfirmationModal"
          :restrictions="formatRestrictionsToSend(restrictions.restricoes)"
          :allotments="formatAllotmentsToSend(restrictions.allotment)"
          :formFields="JSON.parse(JSON.stringify(rates))"
          :rooms="rooms"
          :selectedGuestsOptions="selectedGuestsOptions"
          :hotel="hotel"
          @confirmModal="saveRatesUpdate()"
        />

        <!-- MODAL DE PRÉ-VISUALIZAÇÃO -->
        <PreviewCalendarDialog
          v-model="showPreviewModal"
          :roomOptions="selectedRoomsOptions"
          :guestOptions="selectedGuestsOptions"
          :datePeriods="previewCalendarPeriods"
          :roomsRatesRules="formatRoomRates.tarifas"
          :restrictions="calendarRestrictions()"
          class="preview-calendar-modal"
        />

        <!-- MODAL COM LOADER -->
        <LoaderModal v-model="showLoaderModal" title="Importante">
          <template #text>
            Aguarde enquanto atualizamos as informações. <br />
            Isso pode levar alguns minutos.
          </template>
        </LoaderModal>

        <!-- MODAL DE CONFIRMAÇÃO DO BLACKOUT EM MASSA -->
        <BlackoutConfirmationModal
          v-model="showBlackoutModal"
          :selectedDates="selectedBlackoutDates"
          :selectedRates="selectedBlackoutRates"
          @close-modal="blackoutModalEvent"
        />
      </template>
    </SidebarLayout>

    <AlertChip v-model="showAlertConfirmation">
      Estamos salvando suas alterações, avisaremos assim que terminar.
    </AlertChip>
  </div>
</template>

<script>
import { capitalizeString } from '@/helpers/string.js'
import SidebarLayout from '@/layouts/SidebarLayout'
import BlackoutConfirmationModal from '@/components/hotel/rates/blackout/BlackoutConfirmationModal'
import InfoPanel from '@solucoes/components-lib-tmp/src/components/patterns/InfoPanel'
import SidebarBlock from '@/components/_base/patterns/SidebarBlock'
import Heading from '@/components/_base/elements/Heading'
import Button from '@/components/_base/elements/Button'
import Tab from '@/components/_base/patterns/Tab'
import Tabs from '@/components/_base/patterns/Tabs'
import RatesUpdate from '@/components/hotel/rates/absolute/RatesUpdate'
import ConfirmationModal from '@/components/hotel/rates/absolute/ConfirmationModal'
import RatesRestrictionsClosures from '@/components/hotel/rates/restrictions/RatesRestrictionsClosures'
import PreviewCalendarDialog from '@/components/hotel/rates/PreviewCalendarDialog'
import hotelCommonsMixin from '@/mixins/hotelCommonsMixin'
import hotelRatesMixin from '@/mixins/hotelRatesMixin'
import AlertModal from '@/components/_base/patterns/AlertModal'
import AlertChip from '@solucoes/components-lib-tmp/src/components/elements/AlertChip'
import LoaderModal from '@/components/_base/commonComponents/LoaderModal'
import MassBlackout from '@/components/hotel/rates/blackout/MassBlackout'

export default {
  name: 'AbsoluteRates',
  mixins: [hotelCommonsMixin, hotelRatesMixin],
  components: {
    SidebarLayout,
    BlackoutConfirmationModal,
    InfoPanel,
    SidebarBlock,
    Heading,
    Button,
    RatesUpdate,
    RatesRestrictionsClosures,
    Tab,
    Tabs,
    PreviewCalendarDialog,
    ConfirmationModal,
    AlertModal,
    AlertChip,
    LoaderModal,
    MassBlackout
  },
  data() {
    return {
      showPreviewModal: false /** Modal de pré-visualização do calendário */,
      selectedTab: null,
      rooms: [],
      rates: {
        dates: [],
        editedRates: [],
        rate: [],
        selectedRate: null
      },
      showLoaderModal: false /** Exibição da modal com loader */,
      showAlertConfirmation: false /** Exibição do toast de feedback */,
      showBlackoutModal: false /** Exibição da modal de blackout */,

      selectedBlackoutDates: [] /** Datas selecioandas para o blackout em massa */,
      selectedBlackoutRates: [] /** Tarifas selecionadas para o blackout em massa */,
      blackoutSuccess: false /** Se o blackout foi aplicado com sucesso */
    }
  },
  computed: {
    /**
     * Verifica se existe ao menos 1 quarto com 1 valor selecionado
     * para liberar a aplicação no calendário
     */
    disableApplyToCalendar() {
      let willDisable = false

      /** Se não tem tarifas e restrições, desabilita */
      if (
        this.rates.dates.length === 0 &&
        this.restrictions.allotment.length < 1 &&
        Object.keys(this.restrictions.restricoes).every(
          (item) => this.restrictions.restricoes[item].length < 1
        )
      )
        return true

      /** Se foram selecionados perídos para atualizar valores */
      if (this.rates.dates.length > 0) {
        /** Se não tem valores selecionados cancela */
        const hasValues = this.formatRoomRates.tarifas
          .flatMap((item) => item.quartos)
          .flatMap((rooms) => rooms.valores)
          .some((item) => item.valor !== null && item.valor >= 0)

        const hasDiscount = this.formatRoomRates.tarifas
          .flatMap((item) => item.quartos)
          .some((rooms) => rooms.desconto !== null && rooms.desconto >= 0)

        willDisable = hasValues || hasDiscount ? false : true
      }

      return willDisable
    },

    /** Verifica se existe ao menos 1 quarto com 1 valor selecionado para pré-visualizar */
    disablePreviewButton() {
      let willDisable = false

      /** Se não tem datas, desabilita */
      if (this.rates.dates.length === 0) return true

      /** Se não tem selecionados cancela */
      willDisable = !this.formatRoomRates.tarifas
        .flatMap((item) => item.quartos)
        .flatMap((rooms) => rooms.valores)
        .some((item) => item.valor !== null)

      return willDisable
    },

    /**
     * Retorna um array com as opções dos quartos selecionados
     * e que possue dias da semana e valores selecionados
     * para ser usada na modal de preview do calendário
     */
    selectedRoomsOptions() {
      return this.rooms.reduce((acc, item) => {
        /**
         * Analisa somente o primeiro item do array
         * porque todos períodos possuem os mesmos quartos
         */
        const match = this.formatRoomRates.tarifas[0]?.quartos.findIndex(
          (selectedItem) => selectedItem.codigo === item.codigo
        )

        if (match > -1) {
          const obj = {
            text: item.nome,
            value: item.codigo
          }

          acc.push(obj)
        }

        return acc
      }, [])
    },

    /**
     * Retorna as opções possíveis de hóspedes para os quartos selecionados
     * para ser usada na modal de preview do calendário
     */
    selectedGuestsOptions() {
      const guestsOptions = this.rates.editedRates.reduce((acc, curr) => {
        /** Pega as chaves do objeto e guarda as referentes a hóspedes */
        const paxKeys = Object.keys(curr.rules[0]).filter(
          (item) => item.search(/^(pax|criancas)[0-9]{1,}/gm) > -1
        )

        paxKeys.forEach((key) => {
          const foundAccKey = acc.findIndex((accKey) => accKey.key === key)

          if (foundAccKey < 0) {
            acc.push({
              rooms: [curr.code],
              key: key
            })
          } else {
            acc[foundAccKey].rooms.push(curr.code)
          }
        })
        return acc
      }, [])

      /** Ordena as opções */
      const pax = guestsOptions
        .filter((item) => item.key.search(/^pax/gim) > -1)
        .sort()
      const children = guestsOptions
        .filter((item) => item.key.search(/^criancas/gim) > -1)
        .sort()

      /** Monta um objeto com o formato aceito pelo componente */
      return [...pax, ...children].map((item) => {
        const obj = {}

        let text = item.key.replace('pax', 'Pax ')
        text = text.replace('criancas', 'Criança ')
        text = text.replace('crianca', 'Criança ')

        obj.text = text
        obj.value = item.key
        obj.rooms = item.rooms

        return obj
      })
    },

    /** Retorna as possíveis opções de períodos selecionados */
    previewCalendarPeriods() {
      return this.rates.dates
    },

    /** Retorna as tarifas selecionadas no formato esperado pelo backend */
    formatRoomRates() {
      const obj = {
        tarifas: []
      }

      /** Itera por cada período para criar o objeto */
      this.rates.dates.forEach((period) => {
        const finalObj = {
          dataInicial: period.start,
          dataFinal: period.end,
          quartos: []
        }

        /** Itera pelas regras de tarifas definidas */
        this.rates.editedRates.forEach((room) => {
          /** Itera pelos quartos para pegar os valores e montar o obj */
          room.rules.forEach((roomRule) => {
            const roomObj = {
              codigo: roomRule.roomCode,
              codigoTarifa: this.rates.selectedRate.value,
              diasSemana: roomRule.selectedWeekDays.flatMap((day) => day.value),
              tipoAtualizacaoValor:
                roomRule.desconto !== null && roomRule.desconto >= 0 ? 2 : 1,
              desconto: roomRule.desconto,
              valores: this.buildRoomRulesObj(roomRule)
            }

            /** Somente adiciona os que possuem dias da semana e algum valor inserido */
            if (
              roomObj.diasSemana.length > 0 &&
              (roomObj.valores.length > 0 || roomObj.desconto > 0)
            ) {
              finalObj.quartos.push(roomObj)
            }
          })
        })

        if (finalObj.quartos.length > 0) {
          obj.tarifas.push(finalObj)
        }
      })
      return obj
    },

    /** Desabilita o botão de aplicar blackout se não houver datas e tarifas selecionadas */
    disableApplyBlackout() {
      return (
        !this.selectedBlackoutDates.length || !this.selectedBlackoutRates.length
      )
    }
  },
  async created() {
    await this.$room.get(this.hotelId).then((response) => {
      this.rooms = response.data.filter(function(item) {
        return item.ativo
      })
      this.rooms.map((r) => {
        r.nome = capitalizeString(r.nome.toLowerCase())
      })
    })
  },

  methods: {
    /**
     * Evento de blackout em massa
     * @param {boolean} event: se o blackout foi aplicado ou não
     */
    blackoutModalEvent(event) {
      this.showBlackoutModal = false
      if (event === 'success') {
        this.blackoutSuccess = true
        this.selectedBlackoutDates = []
        this.selectedBlackoutRates = []
      }
    },

    /**
     * Muda as abas e atribui o index à variável
     * @param {number} event: index da aba selecioada
     */
    changeTab(event) {
      this.selectedTab = event
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .lb-breadcrumb--link:nth-child(2) {
  cursor: default;
  pointer-events: none;
}
.info-panel {
  background: $pmw-gray-lightest;

  &--title {
    margin-bottom: 0px;
    color: $pmw-gray-darkest;
  }
}

.sidebar-block {
  padding-top: 25px;
  .menu-nav {
    max-height: calc(100vh - 315px);
    overflow-y: scroll;
  }
}

.page-actions {
  .heading {
    margin-bottom: 15px;
  }

  ::v-deep {
    .button-wrapper {
      & + .button-wrapper {
        margin: 10px 0;
      }
    }
  }
}

.absolute-rate-page {
  ::v-deep {
    .content {
      &--white_card {
        background-color: $gray-lightest;
      }
    }

    .page-title {
      padding: 40px 50px 56px;
    }
  }
}
</style>
